"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Mail, MessageCircle, Phone } from "lucide-react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function TourBookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-card border border-border">
        <h3 className="text-2xl font-semibold text-foreground mb-3 font-default">Request Received.</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-muted-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" className="h-14 bg-transparent border-b-2 border-muted focus:border-primary px-0 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium text-muted-foreground">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" className="h-14 bg-transparent border-b-2 border-muted focus:border-primary px-0 text-base" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Email Address *</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="h-14 bg-transparent border-b-2 border-muted focus:border-primary px-0 text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Phone Number *</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-000-0000" className="h-14 bg-transparent border-b-2 border-muted focus:border-primary px-0 text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="workspace_interest" rules={{
      required: "Please select a program"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Training Program</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-14 w-full bg-transparent border-b-2 border-muted focus:border-primary px-0 text-base">
                  <SelectValue placeholder="Select your goal..." />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="weight_loss">Weight Loss</SelectItem>
                  <SelectItem value="muscle_gain">Muscle Building</SelectItem>
                  <SelectItem value="online_coaching">Online Coaching</SelectItem>
                  <SelectItem value="athletic_perf">Athletic Performance</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" rules={{
      required: "Please select a date"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Preferred Start Date *</FormLabel>
            <FormControl>
              <div className="bg-card p-3 sm:p-4 border border-muted overflow-hidden">
                <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date ? date.toISOString().split("T")[0] : "")} disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))} className="bg-transparent mx-auto" /></SafeHydrate>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="time_slot" rules={{
      required: "Please pick a time slot"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Preferred Time</FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[{
            value: "morning",
            label: "Morning",
            hint: "5am – 11am"
          }, {
            value: "afternoon",
            label: "Afternoon",
            hint: "12pm – 4pm"
          }, {
            value: "evening",
            label: "Evening",
            hint: "5pm – 9pm"
          }].map((opt, i) => <label key={opt.value} data-index={i} htmlFor={`slot-${opt.value}`} className="flex items-center gap-3 bg-card px-4 py-4 cursor-pointer border border-muted has-[[data-state=checked]]:border-primary transition-colors">
                    <RadioGroupItem id={`slot-${opt.value}`} value={opt.value} />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground font-default">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.hint}</span>
                    </div>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-medium text-muted-foreground">Additional Notes</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your fitness history or specific goals..." className="min-h-32 bg-transparent border-b-2 border-muted focus:border-primary px-0 py-4 text-base" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold shadow-lg group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>
            Start Your Journey
            <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>

      <p className="text-xs text-muted-foreground text-center pt-2">
        <span className="text-primary">*</span> Required fields. Your data is secure and used only for your training consultation.
      </p>
    </Form>;
}
export default function MissionControl() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="414" id="mission-control" className="bg-background py-20 sm:py-28 lg:py-32 font-default">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <span className="inline-block text-xs font-mono font-medium tracking-widest text-primary uppercase mb-6">
                — Start Your Journey
              </span>
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-foreground leading-[0.95] mb-8 font-default">
                Forge your <br />
                <span className="italic font-serif font-normal text-primary">new physique.</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-md">
                Peak Force Fitness is built for those who demand results. 
                Schedule your initial assessment and let's engineer your transformation.
                No excuses, just pure performance.
              </p>

              <div className="bg-card p-6 sm:p-7 border border-border shadow-md">
                <p className="text-xs font-mono font-medium tracking-widest text-muted-foreground uppercase mb-5">
                  Direct Contact
                </p>
                <ul className="space-y-4">
                  {[{
                  Icon: Phone,
                  label: "Call us",
                  value: "+1 555-FORCE-01"
                }, {
                  Icon: Mail,
                  label: "Email",
                  value: "train@peakforce.com"
                }, {
                  Icon: MessageCircle,
                  label: "Support",
                  value: "Mon–Sat, 5am–9pm"
                }].map((item, i) => {
                  const Icon = item.Icon;
                  return <li key={i} data-index={i} className="flex items-start gap-4">
                        <div className="flex-shrink-0 size-10 bg-background flex items-center justify-center border border-border">
                          <Icon className="size-4 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                          <p className="text-sm font-medium text-foreground font-default">
                            {item.value}
                          </p>
                        </div>
                      </li>;
                })}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="program_inquiry_form_join_the_force" formId="program_inquiry_form_join_the_force" sectionName="mission-control" className="bg-card p-6 sm:p-10 lg:p-12 shadow-xl border border-border" defaultValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            workspace_interest: "",
            preferred_date: "",
            time_slot: "",
            message: ""
          }} validationRules={{
            first_name: {
              required: true,
              minLength: 2
            },
            last_name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            workspace_interest: {
              required: true
            },
            preferred_date: {
              required: true
            },
            time_slot: {
              required: true
            }
          }} submitText="Start Your Journey" successMessage="Request received. Our team will contact you within 24 hours to finalize your assessment." errorMessage="Connection error. Please try again or call us directly.">
              <TourBookingForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}